import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import {
  BG_VOID_CSS,
  BG_HOVER,
  TEXT_PRIMARY_CSS,
  TEXT_SECONDARY_CSS,
  TEXT_TERTIARY,
  TEXT_TERTIARY_CSS,
  ACCENT_CYAN,
  ACCENT_MAGENTA,
  ACCENT_MAGENTA_CSS,
  TEXT_PRIMARY,
} from '../ui/theme';

// Design tokens: design/handoff/DESIGN_TOKENS.md (single source of truth).

/**
 * Settings options (persisted to local storage / cloud save).
 */
export interface GameSettings {
  musicVolume: number;       // 0.0–1.0
  sfxVolume: number;         // 0.0–1.0
  notifications: boolean;
  haptics: boolean;
  showTutorialHints: boolean;
}

/**
 * Default settings.
 */
export const DEFAULT_SETTINGS: GameSettings = {
  musicVolume: 0.7,
  sfxVolume: 1.0,
  notifications: true,
  haptics: true,
  showTutorialHints: true,
};

/**
 * SettingsScene: Player settings for audio, notifications, and game options.
 */
export class SettingsScene extends Phaser.Scene {
  private settings: GameSettings;

  constructor() {
    super({ key: 'Settings' });
    this.settings = { ...DEFAULT_SETTINGS };
  }

  init(data: { settings?: GameSettings }): void {
    this.settings = data.settings ?? { ...DEFAULT_SETTINGS };
  }

  create(): void {
    this.cameras.main.setBackgroundColor(BG_VOID_CSS);

    // Header
    this.add
      .text(GAME_WIDTH / 2, 50, 'SETTINGS', {
        font: 'bold 32px Inter',
        color: TEXT_PRIMARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(10);

    // Back button
    this.createBackButton();

    let y = 140;
    const rowHeight = 70;

    // Music volume
    this.createSlider(y, 'Music', this.settings.musicVolume, (val) => {
      this.settings.musicVolume = val;
    });
    y += rowHeight;

    // SFX volume
    this.createSlider(y, 'Sound Effects', this.settings.sfxVolume, (val) => {
      this.settings.sfxVolume = val;
    });
    y += rowHeight;

    // Notifications toggle
    this.createToggle(y, 'Push Notifications', this.settings.notifications, (val) => {
      this.settings.notifications = val;
    });
    y += rowHeight;

    // Haptics toggle
    this.createToggle(y, 'Haptic Feedback', this.settings.haptics, (val) => {
      this.settings.haptics = val;
    });
    y += rowHeight;

    // Tutorial hints toggle
    this.createToggle(y, 'Tutorial Hints', this.settings.showTutorialHints, (val) => {
      this.settings.showTutorialHints = val;
    });
    y += rowHeight + 20;

    // Reset progress (dangerous action)
    this.createDangerButton(y, 'Reset Progress', () => {
      this.showResetConfirmation();
    });

    // Footer links
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 80, 'Privacy Policy | Terms of Service', {
        font: '13px Inter',
        color: TEXT_TERTIARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(10);

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 50, 'Splintr v0.7.0 | Made with Phaser', {
        font: '12px Inter',
        color: TEXT_TERTIARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(10);
  }

  /**
   * Create the back button.
   */
  private createBackButton(): void {
    // 44×44 transparent hit zone for accessibility (WCAG 2.5.5).
    const hit = this.add.rectangle(40, 50, 60, 44, 0x000000, 0.001);
    hit.setDepth(9);
    hit.setInteractive({ useHandCursor: true });

    const backBtn = this.add.rectangle(40, 50, 60, 36, BG_HOVER, 0.3);
    backBtn.setStrokeStyle(1, TEXT_TERTIARY, 0.5);
    backBtn.setDepth(10);

    this.add
      .text(40, 50, '< Back', {
        font: '14px Inter',
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(11);

    hit.on('pointerup', () => {
      this.scene.start('Menu');
    });
  }

  /**
   * Create a labeled slider (simplified as 5 discrete steps for now).
   */
  private createSlider(
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void
  ): void {
    const padding = 20;
    const sliderWidth = 200;
    const sliderX = GAME_WIDTH - padding - sliderWidth;

    // Label
    this.add.text(padding, y, label, {
      font: '18px Inter',
      color: TEXT_PRIMARY_CSS,
    }).setDepth(10);

    // Value display
    const valueText = this.add
      .text(GAME_WIDTH - padding, y, `${Math.round(initialValue * 100)}%`, {
        font: '16px Inter',
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(1, 0)
      .setDepth(10);

    // Slider track
    const trackY = y + 36;
    const track = this.add.rectangle(
      sliderX + sliderWidth / 2,
      trackY,
      sliderWidth,
      6,
      BG_HOVER,
      0.8
    );
    track.setDepth(10);

    const fillWidth = sliderWidth * initialValue;
    const fill = this.add.rectangle(
      sliderX + fillWidth / 2,
      trackY,
      fillWidth,
      6,
      ACCENT_CYAN,
      0.8
    );
    fill.setDepth(11);

    const handle = this.add.circle(
      sliderX + fillWidth,
      trackY,
      10,
      ACCENT_CYAN,
      1
    );
    handle.setDepth(12);
    handle.setInteractive({ useHandCursor: true, draggable: true });

    this.input.setDraggable(handle);
    handle.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number) => {
      const clampedX = Phaser.Math.Clamp(dragX, sliderX, sliderX + sliderWidth);
      handle.x = clampedX;

      const newValue = (clampedX - sliderX) / sliderWidth;
      fill.setSize(sliderWidth * newValue, 6);
      fill.x = sliderX + (sliderWidth * newValue) / 2;

      valueText.setText(`${Math.round(newValue * 100)}%`);
      onChange(newValue);
    });
  }

  /**
   * Create a labeled toggle switch.
   */
  private createToggle(
    y: number,
    label: string,
    initialValue: boolean,
    onChange: (value: boolean) => void
  ): void {
    const padding = 20;
    let isOn = initialValue;

    this.add.text(padding, y + 8, label, {
      font: '18px Inter',
      color: TEXT_PRIMARY_CSS,
    }).setDepth(10);

    // Toggle track
    const trackX = GAME_WIDTH - padding - 30;
    const trackY = y + 18;

    // Hit area is padded to 52×44 so the touch target meets 44 px minimum
    // even though the visual track is 30 px tall.
    const track = this.add.rectangle(trackX, trackY, 52, 30, isOn ? ACCENT_CYAN : BG_HOVER, 0.6);
    track.setStrokeStyle(1, isOn ? ACCENT_CYAN : BG_HOVER, 0.4);
    track.setDepth(10);
    track.setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Rectangle(-26, -22, 52, 44), hitAreaCallback: Phaser.Geom.Rectangle.Contains });

    const knob = this.add.circle(
      isOn ? trackX + 12 : trackX - 12,
      trackY,
      11,
      TEXT_PRIMARY,
      0.9
    );
    knob.setDepth(11);

    track.on('pointerup', () => {
      isOn = !isOn;
      onChange(isOn);

      this.tweens.add({
        targets: knob,
        x: isOn ? trackX + 12 : trackX - 12,
        duration: 150,
        ease: Phaser.Math.Easing.Quadratic.Out,
      });

      track.setFillStyle(isOn ? ACCENT_CYAN : BG_HOVER, 0.6);
      track.setStrokeStyle(1, isOn ? ACCENT_CYAN : BG_HOVER, 0.4);
    });
  }

  /**
   * Show a confirmation dialog before resetting progress.
   */
  private showResetConfirmation(): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;

    // Overlay background
    const overlay = this.add.rectangle(centerX, centerY, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);
    overlay.setDepth(200);
    overlay.setInteractive(); // Block clicks to elements behind

    // Dialog panel
    const panel = this.add.graphics();
    panel.setDepth(201);
    panel.fillStyle(0x0D0E14, 1);
    panel.fillRoundedRect(centerX - 140, centerY - 80, 280, 160, 12);
    panel.lineStyle(2, ACCENT_MAGENTA, 0.6);
    panel.strokeRoundedRect(centerX - 140, centerY - 80, 280, 160, 12);

    const title = this.add
      .text(centerX, centerY - 50, 'Reset All Progress?', {
        font: 'bold 18px Inter',
        color: TEXT_PRIMARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(202);

    const desc = this.add
      .text(centerX, centerY - 16, 'This will erase your save,\ncodex, shop, and achievements.', {
        font: '13px Inter',
        color: TEXT_SECONDARY_CSS,
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(202);

    // Confirm button (min-height 44 px for touch target)
    const confirmBg = this.add.rectangle(centerX - 55, centerY + 42, 100, 44, ACCENT_MAGENTA, 0.2);
    confirmBg.setStrokeStyle(1, ACCENT_MAGENTA, 0.6);
    confirmBg.setDepth(202);
    confirmBg.setInteractive({ useHandCursor: true });

    const confirmText = this.add
      .text(centerX - 55, centerY + 42, 'RESET', {
        font: 'bold 14px Inter',
        color: ACCENT_MAGENTA_CSS,
      })
      .setOrigin(0.5)
      .setDepth(203);

    // Cancel button
    const cancelBg = this.add.rectangle(centerX + 55, centerY + 42, 100, 44, BG_HOVER, 0.3);
    cancelBg.setStrokeStyle(1, TEXT_TERTIARY, 0.5);
    cancelBg.setDepth(202);
    cancelBg.setInteractive({ useHandCursor: true });

    const cancelText = this.add
      .text(centerX + 55, centerY + 42, 'CANCEL', {
        font: 'bold 14px Inter',
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(203);

    const dialogElements = [overlay, panel, title, desc, confirmBg, confirmText, cancelBg, cancelText];

    const dismissDialog = () => {
      for (const el of dialogElements) {
        el.destroy();
      }
    };

    cancelBg.on('pointerup', dismissDialog);

    confirmBg.on('pointerup', () => {
      // Clear all saved data
      localStorage.removeItem('splintr_save');
      localStorage.removeItem('splintr_tutorial');
      dismissDialog();
      // Restart from scratch
      this.scene.start('Game');
    });
  }

  /**
   * Create a danger-styled button (e.g., reset progress).
   */
  private createDangerButton(y: number, label: string, onClick: () => void): void {
    const btnX = GAME_WIDTH / 2;

    // Paired shadow (CTA glow) — approximates the blur halo used on the
    // DOM primary CTAs using a slightly larger, lower-alpha duplicate.
    const glow = this.add.rectangle(btnX, y, 216, 54, ACCENT_MAGENTA, 0.14);
    glow.setDepth(9);

    const bg = this.add.rectangle(btnX, y, 200, 44, ACCENT_MAGENTA, 0.10);
    bg.setStrokeStyle(1, ACCENT_MAGENTA, 0.30);
    bg.setDepth(10);
    bg.setInteractive({ useHandCursor: true });

    this.add
      .text(btnX, y, label, {
        font: 'bold 16px Inter',
        color: ACCENT_MAGENTA_CSS,
      })
      .setOrigin(0.5)
      .setDepth(11);

    bg.on('pointerup', onClick);
  }
}
